import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { SOP_STAGE_LABELS } from '@/lib/sopConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Each stage: offset from day 0, duration in days
const STAGE_DEFS = [
  { stage: '00', offset: 0,  duration: 7,  parallel: false },
  { stage: '01', offset: 7,  duration: 21, parallel: false },
  { stage: '02', offset: 28, duration: 28, parallel: true  },
  { stage: '03', offset: 28, duration: 28, parallel: true  },
  { stage: '04', offset: 56, duration: 14, parallel: false },
];
const TOTAL_DAYS = 70; // 10 weeks sequential (02+03 are parallel)
const TOTAL_WEEKS = 10;

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const fmtDate = (date) =>
  date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

const fmtShort = (date) =>
  date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

// Single Gantt bar row
const GanttRow = ({ def, startDate }) => {
  const info = SOP_STAGE_LABELS[def.stage];
  const leftPct = (def.offset / TOTAL_DAYS) * 100;
  const widthPct = (def.duration / TOTAL_DAYS) * 100;
  const stageStart = startDate ? addDays(startDate, def.offset) : null;
  const stageEnd = startDate ? addDays(startDate, def.offset + def.duration) : null;

  return (
    <div className="flex items-center gap-3">
      {/* Label column */}
      <div className="w-36 flex-shrink-0 flex items-center gap-1.5">
        <span
          className="font-mono text-[10px] font-semibold px-1.5 py-0.5 text-white flex-shrink-0"
          style={{ backgroundColor: info.color, borderRadius: 0 }}
        >
          {def.stage}
        </span>
        <span className="font-mono text-xs text-foreground truncate hidden sm:block">
          {info.label.split(' ').slice(0, 2).join(' ')}
        </span>
      </div>

      {/* Bar track */}
      <div className="flex-1 relative h-8 bg-black/[0.03] border border-border overflow-hidden">
        <div
          className="absolute top-0 h-full flex items-center px-2"
          style={{
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            backgroundColor: info.color,
            borderRadius: 0,
            opacity: 0.88,
          }}
        >
          <span className="font-mono text-[10px] text-white truncate">{info.duration}</span>
        </div>
      </div>

      {/* Date range */}
      <div className="w-40 flex-shrink-0 font-mono text-[10px] text-muted-foreground hidden md:block">
        {stageStart ? `${fmtShort(stageStart)} – ${fmtShort(stageEnd)}` : ''}
      </div>
    </div>
  );
};

const ProjectGantt = ({ projectId }) => {
  const [startDate, setStartDate] = useState(null);
  const [dateInput, setDateInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/projects/${projectId}/timeline`, { withCredentials: true });
        if (res.data?.start_date) {
          const d = new Date(res.data.start_date);
          setStartDate(d);
          setDateInput(res.data.start_date.split('T')[0]);
        }
      } catch { /* no timeline set yet — not an error */ }
      finally { setLoading(false); }
    };
    load();
  }, [projectId]);

  const handleSetDate = async (dateStr) => {
    if (!dateStr) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/projects/${projectId}/start-date`,
        { start_date: dateStr },
        { withCredentials: true }
      );
      setStartDate(new Date(dateStr + 'T00:00:00'));
      toast.success('Project start date set');
    } catch {
      toast.error('Failed to set start date');
    } finally {
      setSaving(false);
    }
  };

  // Current-date needle position
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentDayOffset = startDate
    ? Math.round((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const showNeedle =
    currentDayOffset !== null && currentDayOffset >= 0 && currentDayOffset <= TOTAL_DAYS;
  const needleLeftPct = showNeedle ? (currentDayOffset / TOTAL_DAYS) * 100 : null;

  const completionDate = startDate ? addDays(startDate, TOTAL_DAYS) : null;

  // Week-tick positions (0 – 10 weeks)
  const weekTicks = Array.from({ length: TOTAL_WEEKS + 1 }, (_, i) => (i * 7 / TOTAL_DAYS) * 100);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6" data-testid="gantt-panel">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl text-foreground mb-2">Timeline</h2>
              <p className="text-sm text-muted-foreground font-mono">
                Project delivery schedule — {TOTAL_WEEKS} weeks total
              </p>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-muted-foreground" />
              <label htmlFor="gantt-start-date" className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                Start date
              </label>
              <input
                id="gantt-start-date"
                type="date"
                value={dateInput}
                onChange={e => {
                  setDateInput(e.target.value);
                  handleSetDate(e.target.value);
                }}
                disabled={saving}
                className="bg-black/[0.03] border border-border text-foreground text-sm px-3 py-2 font-mono focus:outline-none focus:ring-1 focus:ring-primary/50"
                style={{ borderRadius: 0 }}
                data-testid="gantt-start-date-input"
              />
              {saving && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
            </div>
          </div>

          {!startDate ? (
            <div className="glass border border-border p-12 text-center">
              <CalendarDays className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="font-heading text-lg text-foreground mb-2">No start date set</p>
              <p className="font-mono text-sm text-muted-foreground">
                Set a project start date above to generate the delivery timeline
              </p>
            </div>
          ) : (
            <div className="glass border border-border p-6">
              {/* Timeline ruler */}
              <div className="flex items-center gap-3 mb-1">
                <div className="w-36 flex-shrink-0" />
                <div className="flex-1 relative">
                  <div className="flex justify-between font-mono text-[10px] text-muted-foreground mb-1">
                    <span>{fmtShort(startDate)}</span>
                    <span>{fmtShort(addDays(startDate, TOTAL_DAYS / 2))}</span>
                    <span>{fmtShort(completionDate)}</span>
                  </div>
                  {/* Tick rail */}
                  <div className="relative h-2">
                    <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
                    {weekTicks.map((pct, i) => (
                      <div
                        key={i}
                        className="absolute top-0 w-px h-full bg-border"
                        style={{ left: `${pct}%` }}
                      />
                    ))}
                  </div>
                </div>
                <div className="w-40 flex-shrink-0 hidden md:block" />
              </div>

              {/* Stage rows — with current-date needle overlaid */}
              <div className="relative space-y-2 mt-3">
                {/* Today needle — layered over all rows */}
                {showNeedle && (
                  <div
                    className="absolute top-0 bottom-0 z-10 pointer-events-none"
                    style={{
                      // Needle must account for the 144px (w-36) label column + 3 (gap) on the left
                      // We use calc to position inside the bar-track area only
                      left: `calc(144px + 12px + ${needleLeftPct}% * (100% - 144px - 12px - 160px - 12px) / 100)`,
                    }}
                  >
                    <div className="w-px h-full" style={{ backgroundColor: '#C5A059' }} />
                    <span
                      className="absolute -top-5 font-mono text-[10px] whitespace-nowrap"
                      style={{ color: '#C5A059', transform: 'translateX(-50%)' }}
                    >
                      TODAY
                    </span>
                  </div>
                )}

                {/* Stages 00 and 01 */}
                {STAGE_DEFS.filter(d => !d.parallel && d.stage !== '04').map(def => (
                  <GanttRow key={def.stage} def={def} startDate={startDate} />
                ))}

                {/* Parallel block — stages 02 and 03 */}
                <div className="border border-dashed border-border/50 p-2" style={{ borderRadius: 0 }}>
                  <div className="font-mono text-[10px] text-muted-foreground mb-2 flex items-center gap-2">
                    <span
                      className="px-1.5 py-0.5 text-white text-[10px] font-semibold"
                      style={{ backgroundColor: '#8496AD', borderRadius: 0 }}
                    >
                      PARALLEL
                    </span>
                    <span>Stages 02 and 03 run simultaneously</span>
                  </div>
                  <div className="space-y-2">
                    {STAGE_DEFS.filter(d => d.parallel).map(def => (
                      <GanttRow key={def.stage} def={def} startDate={startDate} />
                    ))}
                  </div>
                </div>

                {/* Stage 04 */}
                {STAGE_DEFS.filter(d => d.stage === '04').map(def => (
                  <GanttRow key={def.stage} def={def} startDate={startDate} />
                ))}
              </div>

              {/* Footer summary */}
              <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-xs text-muted-foreground">
                  {TOTAL_WEEKS} weeks total · Stages 02 &amp; 03 run in parallel
                </span>
                <span className="font-mono text-sm text-foreground font-medium">
                  Est. completion: {fmtDate(completionDate)}
                </span>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectGantt;
